"use client";
import { GuideDownload } from '../../components/GuideDownload';

import { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Film,
  Download,
  Zap,
  Crown,
  Star,
  Play,
  Trash2,
  AlertCircle,
  Loader2,
  LogOut,
} from "lucide-react";
import { useAuth } from '../../contexts/AuthContext';
import { useUsage } from '../../contexts/UsageContext';
import { useClips } from '../../contexts/ClipsContext';
import { useStripe } from '../../contexts/StripeContext';
import { Button } from '../../components/ui/button';
import { signInPathForDestination } from '../../lib/authRedirect';
import { AccountRequestError, accountRequestMessage } from '../../lib/accountRequest';

type AccountPlan = "free" | "hobby" | "starter" | "expert";
type PortalAction = "upgrade" | "manage" | "update";

const accountPlanFromUsage = (
  plan: string | null | undefined
): AccountPlan | null => {
  switch (plan) {
    case "free":
      return "free";
    case "hobby":
    case "pro":
      return "hobby";
    case "starter":
    case "creator":
      return "starter";
    case "expert":
      return "expert";
    default:
      return null;
  }
};

const accountPlanLabel = (plan: AccountPlan): string => {
  switch (plan) {
    case "free":
      return "Free";
    case "hobby":
      return "Hobby";
    case "starter":
      return "Starter";
    case "expert":
      return "Expert";
  }
};

interface DashboardPageProps {
  onBack: () => void;
  onStartClipping: () => void;
  onShowClips?: () => void;
  onShowPricing?: () => void;
}

export default function DashboardPage({
  onBack,
  onStartClipping,
  onShowClips,
  onShowPricing,
}: DashboardPageProps) {
  const { user, signOut, isSigningOut } = useAuth();
  const { createPortalSession } = useStripe();
  const {
    usage,
    error: usageError,
    refreshUsage,
  } = useUsage();
  const {
    clips,
    loading: clipsLoading,
    error: clipsError,
    refreshClips,
    deleteClip,
    totalCount,
  } = useClips();
  const [pendingPortalAction, setPendingPortalAction] =
    useState<PortalAction | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const portalRequestInFlightRef = useRef(false);
  const [portalNeedsSignIn, setPortalNeedsSignIn] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);

  // Fetch only 5 recent clips for dashboard
  useEffect(() => {
    if (user?.id) {
      refreshClips(5); // Only fetch 5 recent clips for dashboard
    }
  }, [user?.id, refreshClips]);

  const openBillingPortal = async (action: PortalAction) => {
    if (portalRequestInFlightRef.current) return;

    portalRequestInFlightRef.current = true;
    setPendingPortalAction(action);
    setPortalError(null);
    setPortalNeedsSignIn(false);

    try {
      await createPortalSession();
    } catch (error) {
      if (!(error instanceof AccountRequestError && error.kind === 'canceled')) {
        setPortalError(accountRequestMessage(error));
        setPortalNeedsSignIn(error instanceof AccountRequestError && error.kind === 'authentication');
      }
    } finally {
      portalRequestInFlightRef.current = false;
      setPendingPortalAction(null);
    }
  };

  const stats = {
    creditsUsed: usage?.credits_used ?? null,
    creditsRemaining: usage?.credits_remaining ?? null,
    maxCredits: usage?.max_credits ?? null,
    currentPlan: user ? (usage?.plan ?? null) : null,
  };
  const accountPlan = accountPlanFromUsage(stats.currentPlan);
  const displayedPlan = accountPlan
    ? accountPlanLabel(accountPlan)
    : null;
  const subscriptionStatus = usage?.subscription_status;
  const isPaidPlan =
    accountPlan === "hobby" ||
    accountPlan === "starter" ||
    accountPlan === "expert";
  const isNoPlanAccount = Boolean(
    usage !== null && (usage.plan === null || accountPlan === "free")
  );
  const hasUnknownPlan = Boolean(
    usage !== null &&
      typeof usage.plan === "string" &&
      accountPlan === null
  );
  const hasActivePaidSubscription =
    isPaidPlan && subscriptionStatus === "active";
  const hasPastDueSubscription =
    isPaidPlan && subscriptionStatus === "past_due";
  const hasExplicitInactiveSubscription =
    isPaidPlan &&
    typeof subscriptionStatus === "string" &&
    subscriptionStatus !== "active" &&
    subscriptionStatus !== "past_due";
  const hasUnavailableSubscriptionStatus =
    isPaidPlan &&
    (subscriptionStatus === undefined || subscriptionStatus === null);
  const canViewPlans = Boolean(user && isNoPlanAccount);
  const portalPending = pendingPortalAction !== null;
  const hideUsableCreditValues =
    isNoPlanAccount ||
    hasUnknownPlan ||
    (isPaidPlan && !hasActivePaidSubscription);
  const hasCreditUsage =
    !hideUsableCreditValues &&
    stats.creditsUsed !== null &&
    stats.maxCredits !== null;
  const creditsExceeded =
    hasCreditUsage &&
    stats.creditsUsed !== null &&
    stats.maxCredits !== null &&
    stats.creditsUsed > stats.maxCredits;
  const creditProgressPercent =
    hasCreditUsage &&
    stats.creditsUsed !== null &&
    stats.maxCredits !== null &&
    stats.maxCredits > 0
      ? Math.min((stats.creditsUsed / stats.maxCredits) * 100, 100)
      : null;
  const currentPlanMainText = isNoPlanAccount
    ? "No plan yet"
    : displayedPlan;
  const currentPlanStatus = isNoPlanAccount
    ? "Pick a plan to get started."
    : !accountPlan
      ? null
      : hasActivePaidSubscription
        ? "Active subscription"
        : hasPastDueSubscription
          ? "Payment past due"
          : hasExplicitInactiveSubscription
            ? "Subscription inactive"
            : "Subscription status unavailable";
  const monthlyUsageUnavailableCopy = isNoPlanAccount
    ? "No active plan."
    : isPaidPlan && !hasActivePaidSubscription
      ? "Usage is unavailable until billing is active."
      : "Usage data unavailable.";

  const recentClips = clips ? clips.slice(0, 5) : [];


  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDeleteClip = async (clipId: string) => {
    if (!window.confirm('Are you sure you want to delete this clip?')) return;
    try {
      setMediaError(null);
      if (!(await deleteClip(clipId))) throw new Error('Deletion failed');
      // Refresh clips after deletion (maintain dashboard limit)
      await refreshClips(5);
    } catch (error) {
      setMediaError("We couldn't delete this clip. Please try again.");
    }
  };

  const handleDownloadClip = async (clip: any) => {
    try {
      setMediaError(null);
      const response = await fetch(clip.stream_url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = clip.filename || `clip-${clip.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setMediaError("We couldn't download this clip. Please try again.");
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 relative z-10">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
                Account
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Manage your plan, credits, and account.
              </p>
              {user?.email && (
                <p className="mt-1 text-sm text-gray-500">{user.email}</p>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 w-full sm:w-auto"
              onClick={() => void signOut()}
              disabled={isSigningOut}
            >
              {isSigningOut ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {isSigningOut ? "Signing out..." : "Sign out"}
            </Button>
          </div>
        </div>

        {mediaError && <p role="alert" className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">{mediaError}</p>}
        {(usageError || clipsError) && (
          <div className="mb-6 space-y-3">
            {usageError && (
              <div
                role="alert"
                className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold">
                      We could not verify your plan and allowance.
                    </p>
                    <p className="mt-1 text-sm">{usageError.message}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => void refreshUsage(true)}
                      >
                        Retry
                      </Button>
                      {usageError.kind === 'authentication' && (
                        <Button asChild>
                          <a href={signInPathForDestination('/dashboard')}>Sign in again</a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {clipsError && (
              <div
                role="alert"
                className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold">
                      We could not update your recent clips.
                    </p>
                    <p className="mt-1 text-sm">{clipsError.message}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => void refreshClips(5)}
                      >
                        Retry
                      </Button>
                      {clipsError.kind === 'authentication' && (
                        <Button asChild>
                          <a href={signInPathForDestination('/dashboard')}>Sign in again</a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Clips Created */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="bg-blue-50 p-2 rounded-lg">
                <Film className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {clipsLoading || clipsError || clips === null
                    ? '—'
                    : totalCount}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Clips in Your Library</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">Total saved clips</div>
          </div>

          {/* Credits Remaining */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="bg-green-50 p-2 rounded-lg">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 fill-current" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  <span data-testid="credits-remaining-value">
                    {hideUsableCreditValues
                      ? "\u2014"
                      : stats.creditsRemaining ?? "\u2014"}
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Credits Remaining</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">This month</div>
          </div>

          {/* Current Plan */}
          {user && currentPlanMainText && currentPlanStatus && (
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <div className="bg-yellow-50 p-2 rounded-lg">
                  <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">
                    {currentPlanMainText}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Current Plan</div>
                </div>
              </div>
              <div
                data-testid="account-plan-status"
                className={`text-xs ${
                  hasPastDueSubscription
                    ? "font-semibold text-red-600"
                    : "text-gray-500"
                }`}
              >
                {currentPlanStatus}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Recent Clips */}
          <div className="lg:col-span-2 order-1">
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                <Film className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Recent Clips
              </h2>

              {clipsLoading && clips === null ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="bg-gray-50 p-4 sm:p-6 rounded-xl mb-4 inline-block">
                    <Film className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 animate-pulse" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-600">Loading your clips...</p>
                </div>
              ) : clipsError && clips === null ? (
                <div className="py-8 text-center text-sm text-gray-600">
                  Retry above to load your recent clips.
                </div>
              ) : recentClips.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="bg-gray-50 p-4 sm:p-6 rounded-xl mb-4 inline-block">
                    <Film className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    No clips yet
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                    Create your first viral clip to see it here
                  </p>
                  <Button
                    onClick={onStartClipping}
                    className="font-semibold py-2 px-4 rounded-xl"
                  >
                    Create Your First Clip
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {recentClips.map((clip) => (
                    <div key={clip.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg gap-3 sm:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                          <Play className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {clip.title || clip.filename}
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-1">
                            <span>{formatDuration(Math.round(clip.duration))}</span>
                            <span className="hidden sm:inline">
                              {new Date(clip.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:flex-shrink-0">
                        {clip.stream_url && (
                          <>
                            <button
                              onClick={() => handleDownloadClip(clip)}
                              className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 sm:p-2 rounded-lg flex-shrink-0 transition-colors"
                              title="Download clip"
                            >
                              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <a
                              href={clip.stream_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 sm:py-1 rounded-lg text-xs sm:text-sm transition-colors flex-1 sm:flex-none text-center"
                            >
                              View
                            </a>
                          </>
                        )}
                        <Button
                          onClick={() => handleDeleteClip(clip.id)}
                          aria-label="Delete clip"
                          variant="ghost"
                          size="sm"
                          className="bg-red-100 hover:bg-red-200 text-red-600 p-1.5 sm:p-2 rounded-lg flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {totalCount > 5 && (
                    <div className="text-center pt-3 sm:pt-4">
                      <Button 
                        onClick={onShowClips}
                        variant="link"
                        className="text-blue-500 hover:text-blue-600 font-medium text-sm sm:text-base p-0"
                      >
                        View All Clips ({totalCount})
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6 order-2">
            {/* Usage Progress */}
            <div
              data-testid="account-billing-area"
              className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
            >
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Monthly Usage
              </h3>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <div className="flex justify-between text-xs sm:text-sm mb-2">
                    <span className="text-gray-600">Credits Used</span>
                    <span className={`font-medium ${
                      creditsExceeded ? 'text-red-600' : 'text-gray-900'
                    }`} data-testid="monthly-usage-value">
                      {hasCreditUsage
                        ? `${stats.creditsUsed}/${stats.maxCredits}`
                        : "\u2014"}
                    </span>
                  </div>
                  {creditProgressPercent === null ? (
                    <p
                      data-testid="monthly-usage-unavailable"
                      className="text-xs text-gray-500"
                    >
                      {monthlyUsageUnavailableCopy}
                    </p>
                  ) : (
                    <div
                      data-testid="monthly-usage-progress"
                      className="w-full bg-gray-100 rounded-full h-2"
                    >
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          creditsExceeded ? 'bg-red-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${creditProgressPercent}%` }}
                      ></div>
                    </div>
                  )}
                </div>

                <div
                  className="pt-2 border-t border-gray-100"
                  aria-busy={portalPending}
                >
                  {canViewPlans && (
                    <>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">
                        Choose a plan to start creating clips.
                      </p>
                      <Button
                        type="button"
                        onClick={onShowPricing}
                        className="min-h-11 w-full bg-blue-500 text-white hover:bg-blue-600 font-medium px-4 rounded-lg text-xs sm:text-sm"
                      >
                        {"View plans \u2192"}
                      </Button>
                    </>
                  )}
                  {user &&
                    hasActivePaidSubscription &&
                    (accountPlan === "hobby" || accountPlan === "starter") && (
                    <>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">
                        Need more credits?
                      </p>
                      <Button
                        type="button"
                        onClick={() => void openBillingPortal("upgrade")}
                        disabled={portalPending}
                        aria-describedby={portalError ? "account-billing-error" : undefined}
                        className="min-h-11 w-full bg-blue-500 text-white hover:bg-blue-600 font-medium px-4 rounded-lg text-xs sm:text-sm mb-2"
                      >
                        {pendingPortalAction === "upgrade" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Opening Stripe...
                          </>
                        ) : (
                          "Upgrade plan \u2192"
                        )}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => void openBillingPortal("manage")}
                        disabled={portalPending}
                        aria-describedby={portalError ? "account-billing-error" : undefined}
                        variant="link"
                        className="min-h-11 w-full px-4 text-xs sm:text-sm text-gray-600 hover:text-blue-600"
                      >
                        {pendingPortalAction === "manage" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Opening Stripe...
                          </>
                        ) : (
                          "Manage billing"
                        )}
                      </Button>
                    </>
                  )}
                  {user &&
                    hasActivePaidSubscription &&
                    accountPlan === "expert" && (
                      <Button
                        type="button"
                        onClick={() => void openBillingPortal("manage")}
                        disabled={portalPending}
                        aria-describedby={portalError ? "account-billing-error" : undefined}
                        variant="link"
                        className="min-h-11 w-full px-4 text-xs sm:text-sm text-gray-600 hover:text-blue-600"
                      >
                        {pendingPortalAction === "manage" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Opening Stripe...
                          </>
                        ) : (
                          "Manage billing"
                        )}
                      </Button>
                  )}
                  {user &&
                    (hasPastDueSubscription ||
                      hasExplicitInactiveSubscription) && (
                      <>
                        <p
                          className={`text-xs sm:text-sm mb-3 ${
                            hasPastDueSubscription
                              ? "font-semibold text-red-600"
                              : "text-gray-600"
                          }`}
                        >
                          {hasPastDueSubscription
                            ? "Payment past due. Update billing to create clips."
                            : "Your subscription is inactive. Update billing to create clips."}
                        </p>
                        <Button
                          type="button"
                          onClick={() => void openBillingPortal("update")}
                          disabled={portalPending}
                          aria-describedby={portalError ? "account-billing-error" : undefined}
                          className="min-h-11 w-full bg-blue-500 text-white hover:bg-blue-600 font-medium px-4 rounded-lg text-xs sm:text-sm"
                        >
                          {pendingPortalAction === "update" ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              Opening Stripe...
                            </>
                          ) : (
                            "Update billing \u2192"
                          )}
                        </Button>
                      </>
                    )}
                  {user && hasUnavailableSubscriptionStatus && (
                    <>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">
                        Subscription status unavailable.
                      </p>
                      <Button
                        type="button"
                        onClick={() => void openBillingPortal("manage")}
                        disabled={portalPending}
                        aria-describedby={portalError ? "account-billing-error" : undefined}
                        variant="link"
                        className="min-h-11 w-full px-4 text-xs sm:text-sm text-gray-600 hover:text-blue-600"
                      >
                        {pendingPortalAction === "manage" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Opening Stripe...
                          </>
                        ) : (
                          "Manage billing"
                        )}
                      </Button>
                    </>
                  )}
                  {portalError && (
                    <div
                      id="account-billing-error"
                      role="alert"
                      className="mt-3 text-sm font-medium text-red-700"
                    >
                      <p>{portalError}</p>
                      {portalNeedsSignIn && <Button asChild variant="link"><a href={signInPathForDestination('/dashboard')}>Sign in again</a></Button>}
                      <a href="mailto:hello@topclip.ai?subject=TopClip%20billing%20help" className="inline-block min-h-11 py-3 text-blue-600 underline">Contact support</a>
                    </div>
                  )}
                  {!user && (
                    <p className="text-gray-600 text-xs sm:text-sm text-center">
                      Please sign in to view your plan details
                    </p>
                  )}
                </div>
              </div>
            </div>

            <GuideDownload />

            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Quick Actions
              </h3>

              <div className="space-y-2">
                <Button
                  onClick={onStartClipping}
                  variant="ghost"
                  className="w-full bg-gray-50 hover:bg-gray-100 text-gray-900 p-3 rounded-lg text-left flex items-center gap-3 justify-start"
                >
                  <Film className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base">Create New Clip</span>
                </Button>

              </div>
            </div>

            {/* Pro Tip */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-blue-100">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                Pro Tip
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                Upload longer videos (30-60 minutes) for better AI clip
                detection. The AI works best with engaging, conversational
                content!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
